/**
 * Automatic-rebind gate (owner decision 4, 2026-09-30).
 *
 * An analysis commit that reads input-stale used to arm an automatic
 * analysis-only rerun (finalizeReportRevision) and the invalidation reaper
 * re-ran any analysis whose bound inputs drifted, whatever drifted. On
 * Goldenrod (2026-09-30) a Public Sentiment rescan changed ONLY the
 * `publicSentiment` section, and Screening and Environmental were each re-run
 * in full (~55 min, ~$12 together) to fold in a scan that found nothing new.
 *
 * Public sentiment and economic evidence are OPTIONAL currency sections for
 * the analyses that merely consult them: a newer copy marks such an analysis
 * refresh-needed ("newer evidence available"), but a change to them alone
 * never starts an automatic rerun; the user reruns when they want it folded
 * in. Provenance-only differences (cache labels, scan history) are not drift
 * at all under section identity v3 (sedContentIdentity.ts). Everything else
 * (grid, intent, typed preferences, power strategy, a domain's own core
 * sections, authority and upstream evidence) is genuine drift and rebinds as
 * before.
 *
 * Pure and dependency-light: finalize, the reaper and Path to GO all read it.
 */
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_SED_REQUIREMENTS } from '../../config/reportTypeSedRequirements';

/** Sections whose drift alone never starts an automatic rebind of an analysis that does not require them. */
export const AUTO_REBIND_OPTIONAL_CURRENCY_SECTIONS: ReadonlySet<string> = new Set([
  'publicSentiment',
  'economicEvidence',
]);

/**
 * The optional currency sections for `domain`: those above that the domain
 * does not REQUIRE (strict map or registry list). Public Sentiment requires
 * `publicSentiment`, so a new scan is genuine drift for its own analysis.
 */
export function optionalCurrencySectionsForDomain(domain: string): ReadonlySet<string> {
  const required = new Set<string>([
    ...((REPORT_TYPE_SED_REQUIREMENTS as Record<string, readonly string[]>)[domain] ?? []),
    ...(REPORT_TYPE_REGISTRY[domain as ReportTypeId]?.requiredSedSections ?? []),
  ]);
  return new Set([...AUTO_REBIND_OPTIONAL_CURRENCY_SECTIONS].filter(section => !required.has(section)));
}

export type AutoRebindDriftClass = 'current' | 'optional-evidence-only' | 'genuine';

/** Only the two known manifest roots are aliases for existing optional
 * sections. Nested paths, unknown roots and malformed lookalikes stay exact. */
function optionalCurrencySectionName(section: string): string {
  if (section === 'sed.publicSentiment') return 'publicSentiment';
  if (section === 'sed.economicEvidence') return 'economicEvidence';
  return section;
}

/**
 * Classify a drift verdict for the automatic-rebind decision.
 *
 * `optional-evidence-only` needs ALL of: a section-scoped verdict, no drift
 * outside the SED sections (intent, typed preferences, power strategy,
 * authority, upstream evidence, versions, a whole-document fallback), at
 * least one changed bound section, and every changed bound section optional
 * for this domain. Any doubt is `genuine` (rebinds as before).
 */
export function classifyAutoRebindDrift(
  domain: string,
  drift: {
    current: boolean;
    changedBoundSections: readonly string[];
    /** True when anything other than named bound SED sections drifted, or the verdict was not section-scoped. */
    otherDrift: boolean;
  },
): AutoRebindDriftClass {
  if (drift.current) return 'current';
  if (drift.otherDrift || drift.changedBoundSections.length === 0) return 'genuine';
  const optional = optionalCurrencySectionsForDomain(domain);
  return drift.changedBoundSections.every(section => optional.has(optionalCurrencySectionName(section)))
    ? 'optional-evidence-only'
    : 'genuine';
}

/** Human names for the optional sections, for the "newer evidence available" copy. */
export function describeOptionalEvidenceSections(sections: readonly string[]): string {
  const names = sections.map(section => {
    const canonical = optionalCurrencySectionName(section);
    return canonical === 'publicSentiment' ? 'public sentiment'
      : canonical === 'economicEvidence' ? 'economic evidence'
        : section;
  });
  return names.length ? names.join(' and ') : 'optional evidence';
}
